/* eslint-disable max-len */
import React, { useRef, forwardRef, useEffect } from 'react'
import {
  TreeGridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Sort,
  Inject,
  SortSettingsModel,
  SearchSettingsModel,
  ToolbarItems,
  Toolbar,
  AggregatesDirective,
  AggregateDirective,
  AggregateColumnsDirective,
  AggregateColumnDirective,
  Aggregate,
  SelectionSettingsModel,
  ColumnModel,
} from '@syncfusion/ej2-react-treegrid'
import {
  TextAlign,
  SearchEventArgs,
  RowSelectEventArgs,
  PageEventArgs,
  ActionEventArgs,
} from '@syncfusion/ej2-grids'
import { Predicate, Query } from '@syncfusion/ej2-data'
import './DataTreeGridComponent.css'

type DataTreeGridComponentProps = {
  data: any[]
  columns: (ColumnModel & { enableNumericSorting?: boolean })[]
  pageSize?: number
  pageCount?: number
  showCheckbox?: boolean
  showCheckboxWidth?: number
  customCSS?: string
  allowPaging?: boolean
  allowSorting?: boolean
  rowHeight?: number
  sortSettings?: SortSettingsModel
  searchOptions?: SearchSettingsModel
  hasFooter?: boolean
  footerData?: Array<{
    field: string
    template: (props: any) => JSX.Element
    textAlign?: TextAlign
  }>
  rowSelected?: (args: RowSelectEventArgs) => void
  rowDeselected?: (args: RowSelectEventArgs) => void
  selectionSettings?: SelectionSettingsModel | undefined
  onRowDoubleClick?: (args: any) => void
  allowTextWrap?: boolean
  queryCellInfo?: any
  dataBound?: () => void
  isLoading?: boolean
  pageChange?: (args: PageEventArgs) => void
  onActionComplete?: (args: ActionEventArgs) => void
  childMapping?: string
  treeColumnIndex?: number
  idMapping?: string
  parentIdMapping?: string
  hasChildMapping?: string
  rowDataBoundExternal?: (args: any) => void
}

const DataTreeGridComponent = forwardRef<
  TreeGridComponent | null,
  DataTreeGridComponentProps
>(
  (
    {
      data,
      columns,
      pageSize,
      pageCount,
      showCheckbox,
      showCheckboxWidth,
      customCSS,
      allowPaging = false,
      allowSorting,
      rowHeight,
      sortSettings,
      searchOptions,
      hasFooter,
      footerData,
      rowSelected,
      rowDeselected,
      selectionSettings,
      onRowDoubleClick,
      allowTextWrap,
      queryCellInfo,
      dataBound,
      isLoading,
      pageChange,
      onActionComplete,
      childMapping,
      treeColumnIndex = 0,
      idMapping,
      parentIdMapping,
      hasChildMapping,
      rowDataBoundExternal,
    },
    ref,
  ) => {
    const treeRef = useRef<TreeGridComponent | null>(null)

    useEffect(() => {
      if (!treeRef.current) return
      if (isLoading) {
        treeRef.current.showSpinner()
      } else {
        treeRef.current.hideSpinner()
      }
    }, [isLoading])

    const rowDataBound = (args: any): void => {
      rowDataBoundExternal?.(args)
      if (customCSS !== 'location-table') return

      const isDisabled = args.data.disabled
      const color = isDisabled ? '#8a8e97' : '#212529'
      const backgroundColor = isDisabled ? '#eff1f3' : '#ffffff'
      const cells = args.row.querySelectorAll('td')

      cells.forEach((cell: any, index: number) => {
        const textAlign = index < 3 ? 'left' : 'center'
        cell.setAttribute(
          'style',
          `color: ${color}; background-color: ${backgroundColor} !important; text-align: ${textAlign};`,
        )
      })
    }

    const headerCellInfo = (args: any): void => {
      args.node.style.backgroundColor = '#eff1f3'
      args.node.style.color = '#4e525a'
      args.node.style.fontWeight = '600'
      args.node.style.fontSize = '0.875rem'
      args.node.style.lineHeight = '24px'
      args.node.style.height = '32px'
      const sortNumber = args.node.querySelector('.e-sortnumber')
      if (sortNumber) {
        sortNumber.style.display = 'none'
      }
    }

    const toolbarOptions: ToolbarItems[] = ['Search']

    const actionBegin = (args: SearchEventArgs): void => {
      if (args.requestType === 'searching' && searchOptions) {
        const searchString = args.searchString || ''
        const keys = searchString.split(',')

        let predicate: Predicate | null = null
        keys.forEach((key) => {
          treeRef.current?.getColumns().forEach((col: any) => {
            if (!col.field) return
            const newPredicate = new Predicate(col.field, 'contains', key, true)
            predicate = predicate ? predicate.or(newPredicate) : newPredicate
          })
        })

        if (predicate && treeRef.current) {
          // Works similarly: set query and refresh
          treeRef.current.query = new Query().where(predicate)
          treeRef.current.refreshColumns()
        }
      }
    }

    const actionComplete = (args: SearchEventArgs): void => {
      const searchElement = document.getElementById(
        treeRef.current?.element.id + '_searchbar',
      ) as HTMLInputElement

      if (searchElement && searchOptions && args.requestType === 'refresh') {
        searchElement.value = searchOptions.key || ''
      }
    }

    const selectionSettingsGrid = { checkboxOnly: true }

    const combinedRef = (node: TreeGridComponent | null): void => {
      treeRef.current = node
      if (ref) {
        if (typeof ref === 'function') {
          ref(node)
        } else {
          ref.current = node
        }
      }
    }

    const handleDataBound = (): void => {
      if (typeof dataBound === 'function') {
        dataBound()
      }
      // EJ2 hides spinner automatically on dataBound; ensure it stays visible while loading
      if (isLoading && treeRef.current) {
        treeRef.current.showSpinner()
      }
    }

    const makeLeafSumAggregate = (field: string) => {
      return () => {
        let total = 0

        data.forEach((row: any) => {
          const isChild = row.parentId !== null && row.parentId !== undefined
          if (isChild) {
            const value = Number(row[field])
            if (!Number.isNaN(value)) {
              total += value
            }
          } else {
            if (row.source !== 2) {
              total += Number(row[field])
            }
          }
        })

        return total
      }
    }

    return (
      <div
        className={`custom-datagrid-container ${customCSS ?? ''} ${isLoading ? 'is-loading' : ''}`}
      >
        <TreeGridComponent
          dataSource={data}
          rowDataBound={rowDataBound}
          allowPaging={allowPaging ?? true}
          allowSorting={allowSorting ?? true}
          pageSettings={{
            pageSize,
            pageCount,
          }}
          sortSettings={sortSettings}
          searchSettings={searchOptions}
          headerCellInfo={headerCellInfo}
          rowHeight={rowHeight}
          toolbar={searchOptions ? toolbarOptions : undefined}
          actionBegin={searchOptions ? actionBegin : undefined}
          actionComplete={
            searchOptions ? actionComplete : (onActionComplete ?? undefined)
          }
          width={'100%'}
          allowResizing={true}
          selectionSettings={
            selectionSettings ? selectionSettings : selectionSettingsGrid
          }
          rowSelected={rowSelected ?? undefined}
          rowDeselected={rowDeselected ?? undefined}
          {...(onRowDoubleClick ? { rowDoubleClick: onRowDoubleClick } : {})}
          allowTextWrap={allowTextWrap}
          queryCellInfo={queryCellInfo}
          dataBound={handleDataBound}
          ref={combinedRef}
          {...(pageChange ? { pageChange } : {})}
          treeColumnIndex={treeColumnIndex}
          {...(childMapping ? { childMapping } : {})}
          {...(idMapping ? { idMapping } : {})}
          {...(parentIdMapping ? { parentIdMapping } : {})}
          {...(hasChildMapping ? { hasChildMapping } : {})}
        >
          <ColumnsDirective>
            {showCheckbox && (
              <ColumnDirective
                type="checkbox"
                width={showCheckboxWidth ?? 38}
                allowSorting={false}
              />
            )}

            {columns.map((col, index) => (
              <ColumnDirective
                {...col}
                key={index}
                textAlign={col.textAlign || 'Left'}
                width={col.width || '150'}
                allowSorting={col.allowSorting ?? true}
                {...(col.enableNumericSorting
                  ? {
                      sortComparer: (a: any, b: any) => {
                        const aNum = parseFloat(a) || 0
                        const bNum = parseFloat(b) || 0
                        return aNum - bNum
                      },
                    }
                  : {})}
              />
            ))}
          </ColumnsDirective>

          {hasFooter && (
            <AggregatesDirective>
              <AggregateDirective>
                <AggregateColumnsDirective>
                  {footerData?.map(({ field, template }) => (
                    <AggregateColumnDirective
                      key={`footer-${field}`}
                      field={field}
                      type="Custom"
                      customAggregate={makeLeafSumAggregate(field)}
                      footerTemplate={template}
                    />
                  ))}
                </AggregateColumnsDirective>
              </AggregateDirective>
            </AggregatesDirective>
          )}

          {allowPaging ? (
            <Inject services={[Page, Sort, Toolbar, Aggregate]} />
          ) : (
            <Inject services={[Sort, Toolbar, Aggregate]} />
          )}
        </TreeGridComponent>
      </div>
    )
  },
)

DataTreeGridComponent.displayName = 'DataTreeGridComponent'

export default DataTreeGridComponent
